import React from "react";
import { Grid, useTheme } from "@mui/material";
import { CssButtonOutlined } from "../CSS/CssButtonOutlined";
import { CssButtonContained } from "../CSS/CssButtonContained";

/**
 * CustomButton component to render the CssButton with theme support.
 * @param {object} props - Component props.
 * @param {string} props.type - The type of the button (e.g., "submit").
 * @param {boolean} props.fullWidth - If true, the button will take the full width of its container.
 * @param {string} props.variant - The variant of the button (e.g., "contained").
 * @param {string} props.color - The color of the button.
 * @param {string} props.disabled - If true disabled of the button.
 * @param {object} props.sx - The style overrides for the button.
 * @param {React.ReactNode} props.children - The content of the button.
 * @returns {JSX.Element} Themed button component.

 */
export default function CustomButton({
  type,
  fullWidth,
  variant,
  color,
  colorTexto,
  sx,
  children,
  onClick,
  disabled,
  width,
  height,
  style,
  backgroundColor,
  bxShadow = false,
  borderRadius,
  gridXS,
  gridMD,
  grid,
  fontFamily,
  fontSize,
  border,
  align,
}) {
  /**
   * Hook que obtiene el tema actual utilizando `useTheme` de Material-UI.
   * @type {Theme} El tema actual utilizado en la aplicación, que incluye configuraciones de color, tipografía, etc.
   */
  const theme = useTheme();
  const colorText = theme.palette.mode === "dark" ? "#fff" : "#000000";
  const boxShadowColor =
    theme.palette.mode === "dark"
      ? "rgba(0, 0, 0, 0.5)"
      : "rgba(0, 111, 191, 0.25)";

  return (
    <Grid
      size={{ xs: gridXS, md: gridMD, xl: grid, lg: grid }}
      sx={
        align
          ? {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }
          : undefined
      }
    >
      {variant === "outlined" ? (
        <CssButtonOutlined
          type={type}
          fullWidth={fullWidth}
          variant={variant}
          color={color}
          boxShadowColor={boxShadowColor}
          borderRadius={borderRadius}
          sx={sx}
          onClick={onClick}
          disabled={disabled}
          width={width}
          height={height}
          backgroundColor={backgroundColor}
          fontFamily={fontFamily}
          fontSize={fontSize}
          border={border}
          style={style}
        >
          {children}
        </CssButtonOutlined>
      ) : (
        <CssButtonContained
          type={type}
          fullWidth={fullWidth}
          variant={variant}
          color={color}
          colorText={colorTexto ? colorTexto : colorText}
          boxShadowColor={bxShadow ? boxShadowColor : undefined}
          borderRadius={borderRadius}
          sx={sx}
          onClick={onClick}
          disabled={disabled}
          width={width}
          height={height}
          backgroundColor={backgroundColor}
          fontFamily={fontFamily}
          fontSize={fontSize}
          border={border}
          style={style}
        >
          {children}
        </CssButtonContained>
      )}
    </Grid>
  );
}
